// src/screens/DesktopDownloadScreen.js
// ============================================================================
// CRITCAST FOR WINDOWS (route Download, path /download) - design S7.6
//
// The long-form page the Go Live modal links to.
// - Reads the public getDesktopClientInfo callable (the old client-side
//   Firestore read of platform/desktopClient was denied by the rules).
// - Size, SHA-256 and the signed state come from DESKTOP_RELEASE only when
//   its version equals the callable's version; DESKTOP_RELEASE.url is never
//   a link (the callable's downloads.windows is).
// - Windows only; other systems see W-09 and can still fetch the file.
// - Inside the CritCast app the page shows the app's version, a link to the
//   Streaming Hub and "Check for updates" instead (W-20..W-22).
// Every string and rule lives in src/utils/critcastLaunch.js.
// ============================================================================

import React, { useEffect, useRef, useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  SafeAreaView,
  ActivityIndicator,
  Platform,
  Linking,
  Image,
} from 'react-native';
import NavBar from '../components/NavBar';
import Footer from '../components/Footer';
import { isDesktopApp } from '../utils/platform';
import { DESKTOP_RELEASE } from '../constants/desktopRelease';
import { loadDesktopClientInfo, recordWindowsDownload } from '../components/GoLiveModal';
import {
  W,
  C,
  G,
  G07_FIRST_SENTENCE,
  SUPPORT_EMAIL,
  ROUTE_HUB,
  W_FEATURE_IDS,
  W_REQUIREMENT_IDS,
  downloadPageModel,
  w20,
  launch,
  recordLaunchResult,
  getStorage,
  readAppVersion,
  checkAppUpdates,
} from '../utils/critcastLaunch';

const GOLD = '#FCD000';
const INK = '#0d0d0d';
const CYAN = '#00BFFF';

// "Setup scan. Finds your ..." -> ["Setup scan.", "Finds your ..."]
function splitLead(text) {
  const i = text.indexOf('. ');
  if (i < 0) return [text, ''];
  return [text.slice(0, i + 1), text.slice(i + 2)];
}

function ListItem({ text, bold }) {
  const [lead, rest] = bold ? splitLead(text) : ['', text];
  return (
    <View style={styles.listItem}>
      <View style={styles.dot} />
      <Text style={styles.listText}>
        {lead ? <Text style={styles.listLead}>{lead}</Text> : null}
        {lead && rest ? ' ' : ''}
        {rest}
      </Text>
    </View>
  );
}

export default function DesktopDownloadScreen({ navigation }) {
  const inDesktop = isDesktopApp();
  const [loading, setLoading] = useState(!inDesktop);
  const [info, setInfo] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [appVersion, setAppVersion] = useState(null);
  const [openState, setOpenState] = useState('idle'); // 'idle' | 'opening' | 'timeout'
  const [checkingUpdates, setCheckingUpdates] = useState(false);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);
  useEffect(() => {
    if (inDesktop) {
      readAppVersion().then((v) => { if (mountedRef.current) setAppVersion(v); });
      return;
    }
    loadDesktopClientInfo()
      .then((data) => {
        if (!mountedRef.current) return;
        setInfo(data);
        setLoadError(false);
      })
      .catch(() => {
        if (!mountedRef.current) return;
        setInfo(null);
        setLoadError(true);
      })
      .finally(() => {
        if (mountedRef.current) setLoading(false);
      });
  }, [inDesktop]);

  const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent || '' : '';
  const model = downloadPageModel({ info, release: DESKTOP_RELEASE, loading, loadError, userAgent });

  const onDownload = () => {
    if (!model.downloadUrl) return;
    recordWindowsDownload();
    try {
      if (Platform.OS === 'web') window.location.assign(model.downloadUrl);
      else Linking.openURL(model.downloadUrl);
    } catch (e) {
      // the button stays; nothing else to do
    }
  };

  const onOpenApp = () => {
    if (openState === 'opening') return;
    setOpenState('opening');
    launch().then((result) => {
      recordLaunchResult(result, getStorage());
      if (mountedRef.current) setOpenState(result === 'handoff' ? 'idle' : 'timeout');
    });
  };

  const onCheckUpdates = () => {
    if (checkingUpdates) return;
    setCheckingUpdates(true);
    checkAppUpdates().then(() => {
      if (mountedRef.current) setCheckingUpdates(false);
    });
  };

  const onEmail = () => {
    try { Linking.openURL(`mailto:${SUPPORT_EMAIL}`); } catch (e) { /* ignore */ }
  };

  if (inDesktop) {
    return (
      <SafeAreaView style={styles.container}>
        <NavBar navigation={navigation} />
        <ScrollView style={styles.scrollView} contentContainerStyle={styles.content}>
          <View style={styles.hero}>
            <Image
              source={require('../../assets/critcast_logo_transparent.webp')}
              style={styles.heroLogo}
              resizeMode="contain"
              accessibilityLabel="CritCast"
            />
            <Text style={styles.heroTitle}>{w20(appVersion)}</Text>
            <TouchableOpacity
              style={styles.primaryBtn}
              onPress={() => navigation.navigate(ROUTE_HUB)}
              activeOpacity={0.85}
              accessibilityRole="button"
            >
              <Text style={styles.primaryBtnText}>{W['W-21']}</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.secondaryBtn, checkingUpdates && styles.btnBusy]}
              onPress={onCheckUpdates}
              disabled={checkingUpdates}
              activeOpacity={0.85}
              accessibilityRole="button"
            >
              <Text style={styles.secondaryBtnText}>{W['W-22']}</Text>
            </TouchableOpacity>
          </View>
          <View style={styles.helpRow}>
            <Text style={styles.helpText} onPress={onEmail}>{W['W-13']}</Text>
          </View>
        </ScrollView>
        <Footer navigation={navigation} />
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <NavBar navigation={navigation} />

      <ScrollView style={styles.scrollView} contentContainerStyle={styles.content}>
        {/* Hero */}
        <View style={styles.hero}>
          <Image
            source={require('../../assets/critcast_logo_transparent.webp')}
            style={styles.heroLogo}
            resizeMode="contain"
            accessibilityLabel="CritCast"
          />
          <Text style={styles.heroTitle} accessibilityRole="header">{W['W-01']}</Text>
          <Text style={styles.heroSubtitle}>{W['W-02']}</Text>

          <TouchableOpacity
            style={[styles.primaryBtn, !model.buttonEnabled && styles.btnDisabled]}
            onPress={onDownload}
            disabled={!model.buttonEnabled}
            activeOpacity={0.85}
            accessibilityRole="button"
          >
            {model.loading
              ? <ActivityIndicator size="small" color={INK} />
              : <Text style={styles.primaryBtnText}>{model.buttonLabel}</Text>}
          </TouchableOpacity>

          {model.versionLine && <Text style={styles.underBtn}>{model.versionLine}</Text>}
          {model.releasedLine && <Text style={styles.underBtnMuted}>{model.releasedLine}</Text>}
          {model.loadErrorLine && <Text style={styles.errorLine}>{model.loadErrorLine}</Text>}
          {model.nonWindowsLine && <Text style={styles.noteLine}>{model.nonWindowsLine}</Text>}
        </View>

        {/* Early build notice (W-10 = C-01..C-05) while a published build is
            unsigned; publisher line once signed; nothing before a build is
            published ("Coming soon") */}
        {(model.showEarlyBuild || model.publisherLine) && (
          <View style={styles.noticeWrap}>
            {model.showEarlyBuild ? (
              <View style={styles.noticeBox}>
                <Text style={styles.noticeTitle}>{C['C-01']}</Text>
                <Text style={styles.noticeText}>{C['C-02']}</Text>
                <Text style={styles.noticeText}>{C['C-03']}</Text>
                <Text style={styles.noticeText}>{C['C-04']}</Text>
                {model.shaLine && <Text style={styles.shaText} selectable>{model.shaLine}</Text>}
              </View>
            ) : (
              <View style={styles.noticeBox}>
                <Text style={styles.noticeText}>{model.publisherLine}</Text>
                {model.shaLine && <Text style={styles.shaText} selectable>{model.shaLine}</Text>}
              </View>
            )}
          </View>
        )}

        {/* What CritCast does */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>{W['W-05']}</Text>
          {W_FEATURE_IDS.map((id) => <ListItem key={id} text={W[id]} bold />)}
        </View>

        {/* Requirements */}
        <View style={[styles.section, styles.sectionAlt]}>
          <Text style={styles.sectionTitle}>{W['W-06']}</Text>
          {W_REQUIREMENT_IDS.map((id) => <ListItem key={id} text={W[id]} />)}
        </View>

        {/* Already have it? */}
        <View style={styles.alreadyHave}>
          <Text style={styles.alreadyHaveText}>{W['W-11']}</Text>
          <TouchableOpacity
            style={[styles.openAppBtn, openState === 'opening' && styles.btnBusy]}
            onPress={onOpenApp}
            disabled={openState === 'opening'}
            activeOpacity={0.85}
            accessibilityRole="button"
          >
            <Text style={styles.openAppBtnText}>
              {openState === 'opening' ? G['G-06'] : W['W-12']}
            </Text>
          </TouchableOpacity>
          {openState === 'timeout' && <Text style={styles.noteLine}>{G07_FIRST_SENTENCE}</Text>}
        </View>

        <View style={styles.helpRow}>
          <Text style={styles.helpText} onPress={onEmail}>{W['W-13']}</Text>
        </View>
      </ScrollView>

      <Footer navigation={navigation} />
    </SafeAreaView>
  );
}

// ============================================================================
// STYLES (dark theme, gold #FCD000 on #0d0d0d, cyan links)
// ============================================================================

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#0d0d0e',
  },
  scrollView: {
    flex: 1,
  },
  content: {
    paddingBottom: 60,
  },

  // Hero
  hero: {
    alignItems: 'center',
    paddingVertical: 48,
    paddingHorizontal: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#1a1a1a',
  },
  heroLogo: {
    width: 320,
    height: 120,
    maxWidth: '100%',
    marginBottom: 20,
  },
  heroTitle: {
    fontSize: 34,
    fontWeight: 'bold',
    color: GOLD,
    marginBottom: 12,
    textAlign: 'center',
  },
  heroSubtitle: {
    fontSize: 17,
    color: '#9a9a9a',
    textAlign: 'center',
    maxWidth: 520,
    marginBottom: 28,
    lineHeight: 25,
  },

  // Buttons
  primaryBtn: {
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: GOLD,
    paddingVertical: 16,
    paddingHorizontal: 32,
    borderRadius: 12,
    minWidth: 280,
    minHeight: 56,
    marginBottom: 12,
  },
  primaryBtnText: {
    fontSize: 18,
    fontWeight: '700',
    color: INK,
  },
  btnDisabled: {
    opacity: 0.55,
  },
  btnBusy: {
    opacity: 0.7,
  },
  secondaryBtn: {
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#1a1a1a',
    borderWidth: 1,
    borderColor: '#333',
    paddingVertical: 13,
    paddingHorizontal: 28,
    borderRadius: 12,
    minWidth: 280,
  },
  secondaryBtnText: {
    fontSize: 16,
    fontWeight: '700',
    color: '#EDEDED',
  },
  underBtn: {
    fontSize: 13,
    color: '#bbb',
    marginTop: 2,
    textAlign: 'center',
  },
  underBtnMuted: {
    fontSize: 12,
    color: '#777',
    marginTop: 4,
    textAlign: 'center',
  },
  errorLine: {
    fontSize: 13,
    color: '#ff8a8a',
    marginTop: 10,
    textAlign: 'center',
  },
  noteLine: {
    fontSize: 13,
    color: '#9a9a9a',
    marginTop: 10,
    textAlign: 'center',
  },

  // Early build notice / publisher. The wrapper keeps a 20 px side gutter at
  // phone widths (like styles.section) and caps the box at 640 px.
  noticeWrap: {
    alignSelf: 'center',
    width: '100%',
    maxWidth: 680,
    marginTop: 28,
    paddingHorizontal: 20,
  },
  noticeBox: {
    padding: 18,
    borderRadius: 12,
    backgroundColor: '#17150a',
    borderWidth: 1,
    borderColor: '#3a3310',
  },
  noticeTitle: {
    fontSize: 15,
    fontWeight: '700',
    color: GOLD,
    marginBottom: 8,
  },
  noticeText: {
    fontSize: 13,
    color: '#cfcfcf',
    lineHeight: 19,
    marginBottom: 6,
  },
  shaText: {
    fontSize: 12,
    color: '#bdbdbd',
    lineHeight: 18,
    marginTop: 4,
    fontFamily: Platform.OS === 'web' ? 'monospace' : undefined,
  },

  // Sections
  section: {
    alignSelf: 'center',
    width: '100%',
    maxWidth: 640,
    paddingVertical: 32,
    paddingHorizontal: 20,
  },
  sectionAlt: {
    backgroundColor: '#0a0a0a',
    borderRadius: 12,
  },
  sectionTitle: {
    fontSize: 22,
    fontWeight: '700',
    color: '#fff',
    marginBottom: 18,
  },
  listItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  dot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: GOLD,
    marginTop: 8,
    marginRight: 12,
  },
  listText: {
    flex: 1,
    fontSize: 14,
    color: '#bdbdbd',
    lineHeight: 21,
  },
  listLead: {
    color: '#fff',
    fontWeight: '700',
  },

  // Already have it
  alreadyHave: {
    alignItems: 'center',
    paddingTop: 32,
    paddingHorizontal: 20,
  },
  alreadyHaveText: {
    fontSize: 14,
    color: '#777',
    marginBottom: 12,
  },
  openAppBtn: {
    paddingVertical: 10,
    paddingHorizontal: 22,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#333',
  },
  openAppBtnText: {
    fontSize: 14,
    color: '#ddd',
    fontWeight: '600',
  },

  // Help
  helpRow: {
    alignItems: 'center',
    paddingTop: 28,
    paddingHorizontal: 20,
  },
  helpText: {
    fontSize: 13,
    color: CYAN,
  },
});
